import { createContext, useContext, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { apiFetch, setAuthToken, SESSION_EXPIRED_EVENT } from "@/lib/api";

const UserContext = createContext();

export function UserProvider({ children }) {
  const [user, setUser] = useState(null);
  const [authLoaded, setAuthLoaded] = useState(false);
  const navigate = useNavigate();

  const fetchUser = async () => {
    try {
      const response = await apiFetch("/api/auth/me");

      if (response.ok) {
        const data = await response.json();
        setUser(data);
      } else {
        setUser(null);
      }
    } catch {
      setUser(null);
    }
    setAuthLoaded(true);
  };

  const ssoLogin = async (email) => {
    try {
      const response = await apiFetch("/api/auth/sso/login", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (response.ok && data.redirectUrl) {
        window.location.href = data.redirectUrl;
        return { ok: true };
      }

      return { ok: false, data };
    } catch (error) {
      console.error("Error during SSO login:", error);
      return { ok: false, error };
    }
  };

  const login = async (email, password) => {
    try {
      const response = await apiFetch("/api/auth/login", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email, password }),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.token) setAuthToken(data.token);
        await fetchUser();
      }
      return { ok: response.ok };
    } catch (error) {
      console.error("Error during login:", error);
      return { ok: false, error };
    }
  };

  const logout = async () => {
    try {
      const response = await apiFetch("/api/auth/logout", {
        method: "POST",
      });

      if (response.ok) {
        setAuthToken(null);
        setUser(null);
        navigate("/login");
        return true;
      } else {
        return false;
      }
    } catch (error) {
      console.error("Error during logout:", error);
      return false;
    }
  };

  const changeAccount = async (accountId) => {
    try {
      const response = await apiFetch("/api/auth/account", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ accountId }),
      });

      if (response.ok) {
        // Hard reload rather than a client-side navigate: switching
        // accounts changes the show list, the current show, and any
        // component state seeded from the old account (e.g. the assets
        // folder, cached in localStorage per show id). A soft navigate
        // to the same route doesn't remount those components, so stale
        // state from the old account bleeds into the new one. Matches
        // `switchShow`'s reload in ShowContext, same reasoning.
        window.location.href = `${import.meta.env.BASE_URL}show/assets`;
        return true;
      }
      return false;
    } catch (error) {
      console.error("Error during account change:", error);
      return false;
    }
  };

  useEffect(() => {
    fetchUser();
  }, []);

  // When apiFetch detects an expired session, clear the user and send them to
  // the login screen with a flag so it can show the "session expired" message.
  useEffect(() => {
    const onSessionExpired = () => {
      setUser(null);
      navigate("/login?reason=session_expired");
    };
    window.addEventListener(SESSION_EXPIRED_EVENT, onSessionExpired);
    return () =>
      window.removeEventListener(SESSION_EXPIRED_EVENT, onSessionExpired);
  }, [navigate]);

  return (
    <UserContext.Provider
      value={{
        user,
        authLoaded,
        fetchUser,
        login,
        changeAccount,
        logout,
        ssoLogin,
      }}>
      {children}
    </UserContext.Provider>
  );
}

export function useUser() {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error("useUser must be used within a UserProvider");
  }
  return context;
}

export const UserRoles = {
  0: "viewer",
  10: "commentator",
  20: "editor",
  30: "manager",
  40: "admin",
};

export function UserRole({ children, role = "viewer" }) {
  const { user } = useUser();
  const roles = {
    viewer: 0,
    commentator: 10,
    editor: 20,
    manager: 30,
    admin: 40,
  };
  if (user && user.role >= roles[role]) {
    return children;
  }
  return null;
}

export function UserOwns({ children, id, fallback = null }) {
  const { user } = useUser();
  if (user && user.id === id) {
    return children;
  } else {
    return fallback;
  }
}
